import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { Home, DollarSign, Calendar, TrendingUp, MoreHorizontal } from 'lucide-react';
import CycleSheet from '@/components/CycleSheet';
import PeriodProgress from '@/components/PeriodProgress';
import './EmployeeHome.css';
import { CuteLoader } from "@/components/CuteLoader";

const ARC_LEN = Math.PI * 75; // semicircle circumference, r=75 (cradles the hero figure only)

function prefersReducedMotion() {
  return typeof window !== 'undefined' && window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function FunFactCarousel({ facts }) {
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  const startX = useRef(null);
  const n = facts.length;
  const reduced = prefersReducedMotion();

  useEffect(() => {
    if (n <= 1 || paused || reduced) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % n), 7000);
    return () => clearInterval(t);
  }, [n, paused, reduced]);

  const go = (i) => setIdx(((i % n) + n) % n);
  const onTouchStart = (e) => { setPaused(true); startX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (startX.current == null) return;
    const dx = e.changedTouches[0].clientX - startX.current;
    if (dx > 40) go(idx - 1);
    else if (dx < -40) go(idx + 1);
    startX.current = null;
  };

  return (
    <div
      className="eh-card eh-funfact-card"
      style={{ animationDelay: '0.75s' }}
      data-testid="eh-fun-fact"
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      onMouseEnter={() => setPaused(true)}
    >
      <div className="eh-fun-eyebrow">{n > 1 ? 'Fun Facts' : 'Fun Fact'}</div>
      <div className="eh-funfact-viewport">
        {facts.map((f, i) => (
          <div
            key={f}
            className="eh-fun-line eh-funfact-slide"
            data-testid={`eh-fun-slide-${i}`}
            style={{
              opacity: i === idx ? 1 : 0,
              transform: `translateX(${(i - idx) * 8}px)`,
              pointerEvents: i === idx ? 'auto' : 'none',
              position: i === idx ? 'relative' : 'absolute',
            }}
            aria-hidden={i !== idx}
          >
            {f}
          </div>
        ))}
      </div>
      {n > 1 && (
        <div className="eh-funfact-dots" data-testid="eh-fun-dots">
          {facts.map((_, i) => (
            <button
              key={i}
              className={`eh-funfact-dot ${i === idx ? 'active' : ''}`}
              data-testid={`eh-fun-dot-${i}`}
              onClick={() => { setPaused(true); go(i); }}
              aria-label={`Fact ${i + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}


function fmtMoney(n, decimals = 0) {
  const v = Number(n || 0);
  return v.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
}
function fmtRecapDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[d.getUTCMonth()]} ${d.getUTCDate()}`;
}

export default function EmployeeHome() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async (periodId) => {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000);
    try {
      const url = periodId ? `/employee/home?period_id=${periodId}` : '/employee/home';
      const { data: d } = await api.get(url, { signal: controller.signal });
      setData(d);
      setError(null);
    } catch (e) {
      setError(e.name === 'AbortError' ? 'Request timed out — pull down to retry' : "Something didn't load — pull down to retry");
    } finally {
      clearTimeout(timeoutId);
      setLoading(false);
    }
  }, []);

  const [cycleSheetOpen, setCycleSheetOpen] = useState(false);
  const [cycles, setCycles] = useState([]);
  const [selectedCycleId, setSelectedCycleId] = useState(null);

  const openCycleSheet = useCallback(async () => {
    if (!cycles.length) {
      try {
        const { data } = await api.get('/employee/cycles');
        setCycles(data.cycles || []);
      } catch (e) { console.warn('Cycle switcher failed to load cycles:', e); }
    }
    setCycleSheetOpen(true);
  }, [cycles.length]);

  const selectCycle = useCallback((id) => {
    setCycleSheetOpen(false);
    setSelectedCycleId(id);
    setLoading(true);
    fetchData(id);
  }, [fetchData]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const [progress, setProgress] = useState(null);
  useEffect(() => {
    let alive = true;
    api.get('/employee/progress').then(({ data }) => { if (alive) setProgress(data); })
      .catch((e) => console.warn('Period progress failed to load:', e));
    return () => { alive = false; };
  }, []);

  if (loading) return <CuteLoader />;

  if (error) {
    return (
      <div className="eh-root" style={{ background: 'linear-gradient(168deg,#231E1A,#3A2A1E)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ color: '#B0905E', fontFamily: 'Cormorant, serif', fontSize: 18, marginBottom: 16 }}>{error}</div>
          <button onClick={() => { setLoading(true); setError(null); fetchData(); }} data-testid="eh-retry"
            style={{ padding: '12px 24px', background: '#9C6B4A', color: '#fff', border: 'none', borderRadius: 8 }}>Retry</button>
        </div>
      </div>
    );
  }

  const { employee, greeting, current_period, arc, signature_earnings, recap, checkin, goal, snapshot, fun_fact, fun_facts, milestone, is_provider } = data;
  const facts = (fun_facts && fun_facts.length) ? fun_facts : (fun_fact ? [fun_fact] : []);
  if (!current_period) {
    return <div className="eh-root" data-testid="eh-no-period" style={{ padding: 40, textAlign: 'center', fontFamily: 'Cormorant, serif' }}>No active pay period yet.</div>;
  }

  // Arc geometry — the ring now fills on PERIOD PROGRESS (elapsed days / total days), never on tier.
  const isFin = current_period.is_finalized;
  const ringFrac = isFin ? 1 : Math.max(0, Math.min(progress?.period?.elapsed_fraction ?? 0, 1));
  // Arc center shows GROSS EARNED so far (same source as the progress card); finalized falls back to real earned.
  // Owner rule: only ADP dollars. Open period shows Homebase hours, never an estimate.
  const arcGross = isFin ? (recap?.gross ?? arc?.commission_earned ?? null) : null;
  const frac = ringFrac;
  const dashoffset = ARC_LEN * (1 - frac);
  const tipX = 93 - 75 * Math.cos(frac * Math.PI);
  const tipY = 100 - 75 * Math.sin(frac * Math.PI);

  const initial = employee.initial;

  return (
    <div className="eh-root" data-testid="employee-home">
      {/* Header */}
      <div className="eh-header">
        <div className="eh-brand-row">
          <div className="eh-wordmark">GT L.A.B Careers</div>
          <div className="eh-avatar" data-testid="eh-avatar">{initial}</div>
        </div>
        <div className="eh-eyebrow" data-testid="eh-eyebrow">{greeting.eyebrow}</div>
        <h1 className="eh-greeting" data-testid="eh-greeting">
          Good {greeting.time_of_day},<br />
          <em>{greeting.endearment}.</em>
        </h1>
        <button className="eh-pill" data-testid="eh-period-pill" onClick={openCycleSheet}
          aria-label="Change pay cycle" title="Tap to switch pay cycle"
          style={{ cursor: 'pointer', font: 'inherit' }}>
          <span className="eh-pill-dot" />
          <span>{current_period.period_status}</span>
          <span className="eh-pill-chev" aria-hidden="true">▾</span>
        </button>
        {selectedCycleId && (
          <button data-testid="eh-back-to-current" onClick={() => selectCycle(null)}
            style={{ marginTop: 8, background: 'none', border: 'none', color: '#B0905E',
                     fontFamily: 'Cormorant, serif', fontSize: 14, cursor: 'pointer', padding: 0 }}>
            ← Back to current
          </button>
        )}
      </div>

      <CycleSheet
        open={cycleSheetOpen}
        onClose={() => setCycleSheetOpen(false)}
        cycles={cycles}
        selectedId={selectedCycleId}
        onSelect={selectCycle}
      />

      {/* Sheet */}
      <div className="eh-sheet">
        {/* CARD 1: Arc (provider) OR signature earnings (non-provider). Ring = period elapsed. */}
        {is_provider ? (
          arc ? (
          <div className="eh-card eh-arc-card" style={{ animationDelay: '0.15s' }} data-testid="eh-arc-card">
            <div className="eh-arc-hero">
              <svg className="eh-arc-svg" width="186" height="112" viewBox="0 0 186 112">
                <defs>
                  <linearGradient id="ehArcGrad" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#9C6B4A" />
                    <stop offset="100%" stopColor="#B0905E" />
                  </linearGradient>
                </defs>
                <path className="eh-arc-track" d="M 18 100 A 75 75 0 0 1 168 100" fill="none" strokeWidth="3" strokeLinecap="round" />
                <path className="eh-arc-fill" d="M 18 100 A 75 75 0 0 1 168 100" fill="none" stroke="url(#ehArcGrad)"
                  strokeWidth="3" strokeLinecap="round" strokeDasharray={ARC_LEN} strokeDashoffset={dashoffset} />
                {frac > 0.02 && <circle cx={tipX} cy={tipY} r="4.5" fill="#B0905E" />}
              </svg>
              <div className="eh-arc-eyebrow">This Period</div>
              <div className="eh-arc-amount" data-testid="eh-arc-earned">
                {arcGross != null ? <><span className="eh-sup">$</span>{fmtMoney(arcGross)}</> : <>{signature_earnings?.hours ?? 0}<span className="eh-sup"> hrs</span></>}
              </div>
            </div>
            <div className="eh-arc-sub" data-testid="eh-arc-earned-label">{arcGross != null ? 'before taxes this period' : 'worked so far this period (Homebase)'}</div>
            {data.time_off_note && <div className="eh-reach" data-testid="eh-time-off-note">{data.time_off_note}</div>}
            {arc.is_estimated ? (
              <div className="eh-est-tag" data-testid="eh-est-tag">Pay is set by ADP with your {arc.finalize_date} check</div>
            ) : (
              <div className="eh-est-tag" data-testid="eh-final-tag">Final · locked</div>
            )}
          </div>
          ) : null
        ) : signature_earnings.variant === 'contractor' ? (
          <div className="eh-card eh-arc-card" style={{ animationDelay: '0.15s' }} data-testid="eh-contractor-card">
            <div className="eh-arc-eyebrow">This Period</div>
            <div className="eh-arc-amount" data-testid="eh-contractor-center">
              {signature_earnings.compensation != null
                ? (<><span className="eh-sup">$</span>{fmtMoney(signature_earnings.compensation)}</>)
                : (<span style={{ fontSize: 22 }}>Compensation pending</span>)}
            </div>
            {signature_earnings.tips > 0 && (
              <div className="eh-arc-sub">tips ${fmtMoney(signature_earnings.tips)} on top</div>
            )}
            {signature_earnings.is_estimated ? (
              <div className="eh-est-tag" data-testid="eh-contractor-tag">Your compensation is set with your {signature_earnings.check_date} check</div>
            ) : (
              <div className="eh-est-tag" data-testid="eh-contractor-final">Final · locked</div>
            )}
          </div>
        ) : (
          <div className="eh-card eh-arc-card" style={{ animationDelay: '0.15s' }} data-testid="eh-signature-card">
            <div className="eh-arc-eyebrow">This Period</div>
            <div className="eh-arc-amount" data-testid="eh-period-earnings">
              {signature_earnings.period_earnings != null
                ? <><span className="eh-sup">$</span>{fmtMoney(signature_earnings.period_earnings)}</>
                : <>{signature_earnings.hours}<span className="eh-sup"> hrs</span></>}
            </div>
            <div className="eh-arc-sub">
              {signature_earnings.period_earnings != null ? `${signature_earnings.hours} hours` : 'Homebase hours'}{signature_earnings.tips > 0 ? ` · tips $${fmtMoney(signature_earnings.tips)} (Booker)` : ''}
            </div>
            {data.time_off_note && <div className="eh-reach" data-testid="eh-time-off-note-sig">{data.time_off_note}</div>}
            {signature_earnings.is_estimated && <div className="eh-est-tag" data-testid="eh-est-tag-sig">Pay is set by ADP with your {signature_earnings.finalize_date} check</div>}
          </div>
        )}

        {/* CARD 1.5: standalone live period progress (original position, restored) */}
        {!selectedCycleId && <PeriodProgress />}

        {/* CARD 3: Recap row */}
        {recap && (
          <div className="eh-card eh-row-card" style={{ animationDelay: '0.25s' }} data-testid="eh-recap-row"
            onClick={() => navigate(`/employee/payday-recap/${recap.period_id}`)}>
            <span className="eh-row-rule" />
            <div className="eh-row-title">Your payday recap</div>
            <div className="eh-row-sub">{fmtRecapDate(recap.date)} · ${fmtMoney(recap.gross)} gross · payday #{recap.payday_number}</div>
            <span className="eh-row-cta">View →</span>
          </div>
        )}

        {/* CARD 3: Check-in row */}
        {checkin && (
          <div className="eh-card eh-row-card" style={{ animationDelay: '0.35s' }} data-testid="eh-checkin-row"
            onClick={() => navigate('/employee/check-in')}>
            <span className="eh-row-rule" />
            <div className="eh-row-title">{checkin.unviewed ? 'Your check-in is ready, love' : 'Your check-in'}</div>
            <div className="eh-row-sub">4 minutes, just for you</div>
            <span className="eh-row-cta">Open →</span>
          </div>
        )}

        {/* CARD 4: Focus goal */}
        {goal && goal.goal_text && (
          <div className="eh-card" style={{ animationDelay: '0.45s' }} data-testid="eh-goal-card">
            <div className="eh-goal-top">
              <span className="eh-goal-name">{goal.goal_text}</span>
            </div>
            <div className="eh-goal-note">You chose this one, babe.</div>
          </div>
        )}

        {/* CARDS 5: Snapshot pairs */}
        <SnapshotPairs snapshot={snapshot} is_provider={is_provider} signature={signature_earnings} />

        {/* CARD 6: Fun fact */}
        {facts.length > 0 && <FunFactCarousel facts={facts} />}

        {/* CARD 7: Milestone */}
        {milestone && (
          <div className="eh-card" style={{ animationDelay: '0.8s' }} data-testid="eh-milestone">
            <div className="eh-milestone">
              <span className="eh-milestone-dot" />
              <div>
                <div className="eh-milestone-when">{milestone.when}</div>
                <div className="eh-milestone-what">{milestone.what}</div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Tab bar */}
      <div className="eh-tabbar">
        <button className="eh-tab active" data-testid="eh-tab-home"><Home size={21} strokeWidth={1.5} /><span className="eh-tab-label">Home</span></button>
        <button className="eh-tab" data-testid="eh-tab-earnings" onClick={() => navigate('/earnings')}><DollarSign size={21} strokeWidth={1.5} /><span className="eh-tab-label">Earnings</span></button>
        <button className="eh-tab" data-testid="eh-tab-timeoff" onClick={() => navigate('/pto')}><Calendar size={21} strokeWidth={1.5} /><span className="eh-tab-label">Time Off</span></button>
        <button className="eh-tab" data-testid="eh-tab-career" onClick={() => navigate('/career')}><TrendingUp size={21} strokeWidth={1.5} /><span className="eh-tab-label">Career</span></button>
        <button className="eh-tab" data-testid="eh-tab-more" onClick={() => navigate('/more')}><MoreHorizontal size={21} strokeWidth={1.5} /><span className="eh-tab-label">More</span></button>
      </div>
    </div>
  );
}

function SnapshotPairs({ snapshot, is_provider, signature }) {
  const tiles = [];
  if (snapshot.pto_hours != null) {
    tiles.push({ key: 'pto', label: 'Time Off', value: `${snapshot.pto_hours} hrs`, sub: `available · as of ${fmtRecapDate(snapshot.pto_as_of)} (ADP)` });
  }
  if (snapshot.pto_used) {
    tiles.push({ key: 'pto-used', label: 'Time Off Used', value: `${snapshot.pto_used.year_total} hrs`, sub: snapshot.pto_used.year_amount != null ? `this year — $${fmtMoney(snapshot.pto_used.year_amount, 2)} paid while you were off` : 'this year · ADP' });
  }
  if (is_provider && snapshot.usual_commission) {
    tiles.push({ key: 'comm', label: snapshot.usual_commission.label, value: `around ${snapshot.usual_commission.pct}%`, sub: snapshot.usual_commission.sub });
  } else if (signature.variant === 'contractor') {
    // Contractors never show an hours tile; surface period tips (dollars, allowed) — never a served count.
    tiles.push({ key: 'tips', label: 'Tips This Period', value: `$${fmtMoney(signature.tips ?? 0)}`, sub: 'tips earned' });
  } else {
    // Sensibility gate failed (trainee / low sales / >100%) or non-provider -> hours/growth card
    tiles.push({ key: 'hrs', label: 'Hours This Period', value: `${snapshot.hours_this_period ?? signature.hours ?? 0}`, sub: 'hours worked' });
  }
  if (snapshot.ytd_gross > 0) {
    tiles.push({ key: 'ytd', label: 'This Year So Far', value: `$${fmtMoney(snapshot.ytd_gross)}`, sub: 'gross this year' });
  }
  if (tiles.length === 0) return null;
  return (
    <div className="eh-snap-grid" data-testid="eh-snapshot">
      {tiles.map((t, i) => (
        <div className="eh-snap" key={t.key} style={{ animationDelay: `${0.55 + i * 0.1}s` }} data-testid={`eh-snap-${t.key}`}>
          <div className="eh-snap-label">{t.label}</div>
          <div className="eh-snap-value">{t.value}</div>
          {t.sub ? <div className="eh-snap-sub">{t.sub}</div> : null}
        </div>
      ))}
    </div>
  );
}
